import React, { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, SlidersHorizontal, X } from "lucide-react";
import {
  Breadcrumbs, Container, Eyebrow, H2, H3, Section, Spinner, EmptyState,
} from "../components/Primitives";
import { ProductGrid } from "../components/ProductCard";
import { VideoBand } from "../components/VideoBand";
import { Ornament } from "../components/Ornament";
import { MukhiQuickNav } from "../components/QuickNav";
import { getProducts, getStoneMeta } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import {
  AnimatePresence, LineReveal, Reveal, Stagger, StaggerItem, drawerBottom, motion, scrim,
} from "../lib/motion";
import { categoryFaqs, categorySections } from "../content/categoryContent";
import {
  BUDGET_NAV, INTENT_NAV, PLANET_NAV, RASHI_NAV, STONE_NAV,
} from "../content/nav";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price low–high" },
  { value: "price-desc", label: "Price high–low" },
  { value: "per-carat-asc", label: "Price per carat" },
  { value: "carat-desc", label: "Carat weight" },
  { value: "best-selling", label: "Best selling" },
];

const CARAT_LABEL = {
  "2-carat": "2 carat", "3-carat": "3 carat", "4-carat": "4 carat",
  "5-carat": "5 carat", "6-carat": "6 carat",
};

/* ------------------------------------------------------------ data hygiene */
const NORMALISE = {
  origin: {
    java: "Java (Indonesia)",
    nepal: "Nepal",
  },
};
const prettify = (group, value) => {
  const v = String(value ?? "").trim();
  const m = NORMALISE[group]?.[v.toLowerCase()];
  return m || v;
};

/* ---------------------------------------- multi-select checkbox filter group */
function CheckboxRow({ checked, count, label, onToggle, testId }) {
  return (
    <label className="fbox" data-testid={testId}>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onToggle}
      />
      <span className="fbox__box" aria-hidden="true">
        <Check strokeWidth={3} />
      </span>
      <span className="fbox__label">{label}</span>
      {count != null && <span className="fbox__count">{count}</span>}
    </label>
  );
}

function FacetGroup({ title, options, active = [], onPick, testId, group, initialShown = 8 }) {
  const [showMore, setShowMore] = useState(false);
  if (!options || Object.keys(options).length < 2) return null;
  const entries = Object.entries(options);
  const shown = showMore ? entries : entries.slice(0, initialShown);
  return (
    <div className="border-b border-rule py-5" data-testid={testId}>
      <p className="menu-h !border-b-0 !pb-0 !mb-3">{title}</p>
      <ul className="space-y-0">
        {shown.map(([value, count]) => {
          const label = prettify(group, value);
          const isOn = active.includes(value);
          return (
            <li key={value}>
              <CheckboxRow
                checked={isOn}
                count={count}
                label={label}
                onToggle={() => onPick(isOn ? active.filter((v) => v !== value) : [...active, value])}
                testId={`${testId}-${value}`}
              />
            </li>
          );
        })}
      </ul>
      {entries.length > initialShown && (
        <button
          type="button"
          onClick={() => setShowMore((s) => !s)}
          className="ui mt-2 text-xs text-gold hover:underline"
          data-testid={`${testId}-toggle`}
        >
          {showMore ? "Show less" : `Show ${entries.length - initialShown} more`}
        </button>
      )}
    </div>
  );
}

function PriceRange({ min, max, value, onChange }) {
  const [lo, setLo] = useState(value?.[0] || "");
  const [hi, setHi] = useState(value?.[1] || "");
  const apply = () => {
    const l = lo === "" ? null : Math.max(0, Number(lo));
    const h = hi === "" ? null : Math.max(l ?? 0, Number(hi));
    onChange([l, h]);
  };
  return (
    <div className="border-b border-rule py-5" data-testid="facet-price">
      <p className="menu-h !border-b-0 !pb-0 !mb-3">Price</p>
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="numeric"
          placeholder={String(min)}
          value={lo}
          onChange={(e) => setLo(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => e.key === "Enter" && apply()}
          className="field num !py-1.5 !text-xs"
          aria-label="Minimum price"
          data-testid="facet-price-min"
        />
        <span className="text-dim text-xs">to</span>
        <input
          type="number"
          inputMode="numeric"
          placeholder={String(max)}
          value={hi}
          onChange={(e) => setHi(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => e.key === "Enter" && apply()}
          className="field num !py-1.5 !text-xs"
          aria-label="Maximum price"
          data-testid="facet-price-max"
        />
      </div>
      <p className="ui mt-2 text-[11px] text-dim">₹, tax inclusive</p>
    </div>
  );
}

/* ------------------------------------------------------ category configuration */
function useCategoryConfig() {
  const params = useParams();
  const { pathname, search } = useLocation();
  const q = new URLSearchParams(search).get("q") || "";

  return useMemo(() => {
    const p = pathname.replace(/\/$/, "");
    const base = { query: {}, trail: [{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }], kind: "mixed", search: q };

    if (p.startsWith("/shop/gemstones")) {
      const stone = params.stone;
      const carat = params.carat;
      const cfg = {
        ...base, kind: "gemstone",
        query: { type: "gemstone", ...(stone ? { stone } : {}), ...(carat ? { carat_bucket: carat } : {}) },
      };
      cfg.trail = [...base.trail, { label: "Gemstones", to: "/shop/gemstones" }];
      const meta = STONE_NAV.find((s) => s.slug === stone);
      if (meta) cfg.trail.push({ label: meta.name, to: `/shop/gemstones/${stone}` });
      if (carat) cfg.trail.push({ label: CARAT_LABEL[carat] || carat });
      cfg.stoneMeta = meta;
      cfg.carat = carat;
      cfg.eyebrow = meta ? "By stone" : "By category";
      cfg.h1 = carat && meta
        ? `${CARAT_LABEL[carat] || carat} · ${meta.name}`
        : meta
          ? `${meta.name} · ${meta.hindi}`
          : "Certified gemstones";
      cfg.title = carat && meta
        ? `${CARAT_LABEL[carat]} ${meta.name} — Price, Certification & Availability`
        : meta
          ? `${meta.name} (${meta.hindi}) — Certified, Verified on the Lab's Own Site`
          : "Certified Gemstones Online — Verified on the Laboratory's Own Site";
      return cfg;
    }

    if (p.startsWith("/shop/rudraksha")) {
      const seg = params.mukhi;
      const cfg = { ...base, kind: "rudraksha", query: { type: "rudraksha" }, eyebrow: "Rudraksha" };
      cfg.trail = [...base.trail, { label: "Rudraksha", to: "/shop/rudraksha" }];
      if (seg === "nepal" || seg === "java") {
        cfg.query.origin = seg;
        cfg.trail.push({ label: seg === "nepal" ? "Nepal" : "Java" });
        cfg.h1 = seg === "nepal" ? "Nepal rudraksha" : "Java (Indonesia) rudraksha";
        cfg.title = cfg.h1;
      } else if (seg) {
        const n = parseInt(seg, 10);
        cfg.query.mukhi = n;
        cfg.trail.push({ label: `${n} mukhi` });
        cfg.h1 = `${n} mukhi rudraksha`;
        cfg.title = `${n} Mukhi Rudraksha — Nepal & Java, X-Ray Verified`;
      } else {
        cfg.h1 = "Rudraksha";
        cfg.title = "Rudraksha Online — X-Ray Verified, Nepal & Java";
      }
      return cfg;
    }

    const simple = {
      "/shop/malas": ["mala", "Malas & kanthas", "By category"],
      "/shop/bracelets": ["bracelet", "Bracelets", "By category"],
      "/shop/pendants": ["pendant", "Pendants & ring settings", "By category"],
      "/shop/yantras": ["yantra", "Yantras", "By category"],
    };
    if (simple[p]) {
      const [type, h1, eyebrow] = simple[p];
      return { ...base, query: { type }, eyebrow, h1, title: h1, trail: [...base.trail, { label: h1 }] };
    }

    if (p === "/shop/new") {
      return { ...base, query: { sort: "newest" }, eyebrow: "Latest", h1: "New arrivals",
        title: "New Arrivals — Certified Gemstones & Rudraksha",
        trail: [...base.trail, { label: "New arrivals" }] };
    }
    if (p === "/shop/sold-archive") {
      return {
        ...base, query: { status: "sold" }, eyebrow: "Permanent archive",
        h1: "Sold archive",
        title: "Sold Archive — Certificates Still Verifiable",
        trail: [...base.trail, { label: "Sold archive" }],
        intro: "We never delete a product URL. Every stone we have sold keeps its page, its photographs and its laboratory report — still verifiable on the laboratory's own site.",
      };
    }
    if (params.graha) {
      const g = PLANET_NAV.find((x) => x.slug === params.graha);
      const grahaName = (g?.label || params.graha).split(" · ")[0];
      return { ...base, query: { graha: params.graha },
        eyebrow: "By planet · traditionally indicated",
        h1: `${grahaName} · stones and items traditionally read with ${grahaName}`,
        title: `${grahaName} — Stones and Items Traditionally Read with ${grahaName}`,
        trail: [...base.trail, { label: "By planet" }, { label: grahaName }] };
    }
    if (params.sign) {
      const r = RASHI_NAV.find((x) => x.slug === params.sign);
      return { ...base, query: { rashi: params.sign },
        eyebrow: "By rashi · traditionally indicated",
        h1: r?.label || params.sign,
        title: `${r?.label || params.sign} Stones — Certified and Verifiable`,
        trail: [...base.trail, { label: "By rashi" }, { label: r?.label || params.sign }] };
    }
    if (params.intent) {
      const i = INTENT_NAV.find((x) => x.slug === params.intent);
      return { ...base, query: { intent: params.intent },
        eyebrow: "By intent",
        h1: i?.label || params.intent,
        title: `${i?.label || params.intent} — Certified Gemstones & Rudraksha`,
        trail: [...base.trail, { label: "By intent" }, { label: i?.label || params.intent }] };
    }
    if (params.tier) {
      const t = BUDGET_NAV.find((x) => x.slug === params.tier);
      return { ...base, query: { budget: params.tier },
        eyebrow: "By budget",
        h1: t?.label || params.tier,
        title: `${t?.label || params.tier} — Certified Gemstones & Rudraksha`,
        trail: [...base.trail, { label: "By budget" }, { label: t?.label || params.tier }] };
    }
    return {
      ...base, query: {},
      eyebrow: q ? "Search" : "Shop",
      h1: q ? `Search: ${q}` : "Everything we currently hold",
      title: q ? `Search results for ${q}` : "Shop All — Certified Gemstones, Rudraksha, Malas & Yantras",
      trail: [...base.trail, { label: q ? "Search" : "All" }],
    };
  }, [pathname, params, q]);
}

function MukhiBand() {
  return (
    <Section className="!py-[70px] border-b border-rule bg-surface">
      <Container>
        <Reveal className="text-center">
          <p className="eyebrow-wide">Browse by mukhi</p>
          <H2 className="section-title mt-5">One to fourteen, plus the named specials</H2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-[1.75] text-dim">
            Each mukhi carries a traditional deity and graha association — stated as tradition, never as
            an effect.
          </p>
        </Reveal>
        <div className="mt-10"><MukhiQuickNav /></div>
      </Container>
    </Section>
  );
}

const ARRAY_FACETS = ["origin", "treatment", "lab", "carat_bucket", "mukhi", "budget"];

export default function Category() {
  const cfg = useCategoryConfig();
  const { pathname: route } = useLocation();
  const cleanRoute = route.replace(/\/$/, "");
  const { format } = useCurrency();
  const [sort, setSort] = useState(cfg.query.sort || "newest");
  const [facets, setFacets] = useState({});
  const [priceRange, setPriceRange] = useState([null, null]);
  const [drawer, setDrawer] = useState(false);

  // Build API query. For OR-within-group, we take the FIRST selected value (backend accepts single).
  // (Facet arrays UX-only; API filters are single value per group in current backend contract.)
  const facetsSingle = Object.fromEntries(
    Object.entries(facets)
      .filter(([, v]) => Array.isArray(v) && v.length)
      .map(([k, v]) => [k, k === "mukhi" ? Number(v[0]) : v[0]]),
  );
  const query = {
    ...cfg.query, sort, limit: 60, ...facetsSingle,
    ...(priceRange[0] != null ? { price_min: priceRange[0] } : {}),
    ...(priceRange[1] != null ? { price_max: priceRange[1] } : {}),
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["products", query],
    queryFn: () => getProducts(query),
    retry: 2,
  });

  const stoneMeta = useQuery({
    queryKey: ["stone", cfg.query.stone],
    queryFn: () => getStoneMeta(cfg.query.stone),
    enabled: Boolean(cfg.query.stone),
  });

  const items = useMemo(() => {
    const list = data?.items || [];
    if (!cfg.search) return list;
    const needle = cfg.search.toLowerCase();
    return list.filter((p) =>
      [p.title, p.subtitle, p.sku, p.certificate?.report_number, p.description]
        .filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [data, cfg.search]);

  const priceMin = data?.price_range?.min || 0;
  const priceMax = data?.price_range?.max || 0;

  const sections = categorySections({
    label: cfg.stoneMeta?.name || cfg.h1?.split(" · ")[0] || "This category",
    hindi: cfg.stoneMeta?.hindi,
    kind: cfg.kind,
    priceMin, priceMax,
    perCaratMin: null, perCaratMax: null, lab: null,
  });
  const faqs = categoryFaqs({
    label: cfg.stoneMeta?.name || "these items",
    kind: cfg.kind,
    perCaratMin: null,
  });

  useSEO({
    title: cfg.title,
    description: stoneMeta.data
      ? `Buy certified ${stoneMeta.data.name.toLowerCase()} with a laboratory report you can verify on the lab's own site. Treatment disclosed in plain language. Price per carat published. 30-day returns.`
      : `${cfg.h1}. Reports verified on the laboratory's own website, treatment disclosed in plain language, price per carat published, 30-day returns.`,
    jsonLd: [
      breadcrumbSchema(cfg.trail),
      { "@context": "https://schema.org", "@type": "CollectionPage", name: cfg.h1, numberOfItems: items.length },
      faqs.length ? {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      } : null,
    ],
  });

  /* Build the chip list. */
  const chips = [];
  for (const g of ARRAY_FACETS) {
    for (const v of facets[g] || []) {
      chips.push({ group: g, value: v, label: prettify(g === "mukhi" ? "" : g, v) + (g === "mukhi" ? " mukhi" : "") });
    }
  }
  if (priceRange[0] != null || priceRange[1] != null) {
    chips.push({
      group: "price", value: "price",
      label: `${priceRange[0] != null ? format(priceRange[0]) : format(0)}${priceRange[1] != null ? ` – ${format(priceRange[1])}` : "+"}`,
    });
  }

  const removeChip = (c) => {
    if (c.group === "price") return setPriceRange([null, null]);
    setFacets((f) => ({ ...f, [c.group]: (f[c.group] || []).filter((x) => x !== c.value) }));
  };

  const pickGroup = (group) => (values) =>
    setFacets((f) => ({ ...f, [group]: values }));

  const rail = (
    <div>
      <FacetGroup title="Origin" options={data?.facets?.origin} active={facets.origin}
        onPick={pickGroup("origin")} testId="facet-origin" group="origin" />
      <FacetGroup title="Treatment" options={data?.facets?.treatment} active={facets.treatment}
        onPick={pickGroup("treatment")} testId="facet-treatment" group="treatment" />
      <FacetGroup title="Certification lab" options={data?.facets?.lab} active={facets.lab}
        onPick={pickGroup("lab")} testId="facet-lab" group="lab" />
      <FacetGroup title="Carat band" options={data?.facets?.carat_bucket} active={facets.carat_bucket}
        onPick={pickGroup("carat_bucket")} testId="facet-carat" group="carat_bucket" />
      <FacetGroup title="Mukhi" options={data?.facets?.mukhi} active={facets.mukhi}
        onPick={pickGroup("mukhi")} testId="facet-mukhi" group="mukhi" />
      <PriceRange min={priceMin} max={priceMax} value={priceRange} onChange={setPriceRange} />
      <div className="border-b border-rule py-5">
        <p className="menu-h !border-b-0 !pb-0 !mb-3">Budget</p>
        <ul className="space-y-0">
          {BUDGET_NAV.map((b) => {
            const isOn = (facets.budget || []).includes(b.slug);
            return (
              <li key={b.slug}>
                <CheckboxRow
                  checked={isOn}
                  label={b.label}
                  onToggle={() => setFacets((f) => ({
                    ...f, budget: isOn ? (f.budget || []).filter((x) => x !== b.slug) : [...(f.budget || []), b.slug],
                  }))}
                  testId={`facet-budget-${b.slug}`}
                />
              </li>
            );
          })}
        </ul>
      </div>
      {cfg.kind === "rudraksha" && (
        <div className="border-b border-rule py-5">
          <p className="menu-h !border-b-0 !pb-0 !mb-3">Verification</p>
          <CheckboxRow
            checked={Boolean(facets.xray)}
            label="X-ray published only"
            onToggle={() => setFacets((f) => ({ ...f, xray: f.xray ? undefined : true }))}
            testId="facet-xray"
          />
        </div>
      )}
    </div>
  );

  /* Header video-band routing */
  const videoHeader = {
    "/shop/rudraksha": { src: "ghats-varanasi-night", eyebrow: "Rudraksha", lines: ["X-rayed, priced by mukhi,", "never by size"], desc: "Every bead above ₹3,000 publishes its own radiograph, so you can count the seed chambers yourself.", scrim: 0.42 },
    "/shop/gemstones": { src: "gems-macro-sparkle", eyebrow: "Certified gemstones", lines: ["Nine navratna,", "one honest price."], desc: "Every stone verifies on the laboratory's own website. Treatment disclosed in plain language, price per carat published.", scrim: 0.42 },
    "/shop/malas": { src: "mala-incense", eyebrow: "Malas & kanthas", lines: ["Hand-knotted,", "bead-matched, counted."], desc: "One hundred and eight beads, size-matched to within 0.4mm. No dyeing, no polishing compound, no lacquer.", scrim: 0.38 },
  }[cleanRoute];

  return (
    <>
      {videoHeader && (
        <VideoBand
          src={`/video/${videoHeader.src}.mp4`}
          webm={`/video/${videoHeader.src}.webm`}
          poster={`/video/${videoHeader.src}-poster.jpg`}
          scrim={videoHeader.scrim}
          surround="#faf7f1"
          testId={`${cfg.kind}-band`}
          className="border-b border-rule"
        >
          <Container className="py-[110px] text-center max-md:py-[70px]">
            <Reveal>
              <Ornament className="mb-7" />
              <p className="eyebrow-wide">{videoHeader.eyebrow}</p>
              <h1 className="hero-h1 mx-auto mt-6 max-w-[var(--measure-tight)] !text-[#f3efe7]">
                <LineReveal lines={videoHeader.lines} inViewAmount={0.4} stagger={0.12} duration={0.9} />
              </h1>
              <p className="hero-desc ui mx-auto mt-7 text-[#f3efe7]/85">{videoHeader.desc}</p>
            </Reveal>
          </Container>
        </VideoBand>
      )}

      <Container className="pt-6">
        <Breadcrumbs trail={cfg.trail} />
      </Container>

      {!videoHeader && (
        <Container className="pb-6 pt-4" data-testid="category-header">
          <Reveal>
            <p className="eyebrow-wide">{cfg.eyebrow}</p>
            <h1 className="mt-4 h1-page" data-testid="category-h1">{cfg.h1}</h1>
            <p className="mt-4 max-w-[var(--measure-read)] text-[15px] leading-[1.75] text-dim" data-testid="category-facts">
              {cfg.intro ? (
                cfg.intro
              ) : (
                <>
                  <span className="num text-ink">{items.length}</span>{" "}
                  {items.length === 1 ? "piece" : "pieces"} currently held
                  {priceMin ? (
                    <>
                      {" · "}
                      <span className="num text-ink">{format(priceMin)}–{format(priceMax)}</span>
                    </>
                  ) : null}
                  {" · every listing shows origin, treatment, lab and report number."}
                </>
              )}
            </p>
          </Reveal>

          {cfg.query.stone && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="carat-links">
              {Object.keys(CARAT_LABEL).map((c) => (
                <Link
                  key={c}
                  to={`/shop/gemstones/${cfg.query.stone}/${c}`}
                  className={`ui border px-3.5 py-2 text-xs transition-colors duration-300 ${
                    cfg.carat === c ? "border-gold text-gold" : "border-rule text-dim hover:border-gold hover:text-gold"
                  }`}
                >
                  {CARAT_LABEL[c]}
                </Link>
              ))}
            </div>
          )}
        </Container>
      )}

      {videoHeader && cfg.kind === "rudraksha" && <MukhiBand />}

      <Section className="pt-12" flat>
        <Container className="grid items-start gap-10 lg:grid-cols-[240px_1fr] lg:gap-14">
          <aside className="hidden lg:sticky lg:top-28 lg:block lg:self-start" data-testid="filter-rail">
            <div className="flex items-baseline justify-between">
              <p className="ui text-sm font-medium text-ink">Filter</p>
              {(chips.length > 0) && (
                <button type="button" onClick={() => { setFacets({}); setPriceRange([null, null]); }} className="ui text-xs text-gold hover:underline" data-testid="clear-facets">
                  Clear all
                </button>
              )}
            </div>
            {rail}
          </aside>

          <div>
            <div className="mb-6 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setDrawer(true)}
                data-testid="open-filter-drawer"
                className="ui flex items-center gap-2 border border-rule px-3 py-2 text-xs text-dim lg:hidden"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" /> Filter
                {chips.length ? <span className="num text-gold">· {chips.length}</span> : null}
              </button>
              <label className="ui ml-auto flex items-center gap-2 text-xs text-dim">
                Sort
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  data-testid="sort-select"
                  className="ui border border-rule bg-transparent px-2 py-1.5 text-xs text-ink"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
            </div>

            {chips.length > 0 && (
              <div className="mb-5 flex flex-wrap gap-2" data-testid="filter-chips">
                {chips.map((c) => (
                  <button
                    key={`${c.group}-${c.value}`}
                    type="button"
                    onClick={() => removeChip(c)}
                    className="chip"
                    data-testid={`chip-${c.group}-${c.value}`}
                  >
                    {c.label}
                    <X className="h-3 w-3 chip__x" aria-hidden="true" />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setFacets({}); setPriceRange([null, null]); }}
                  className="ui text-xs text-gold hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}

            {isLoading ? (
              <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-skeleton" aria-busy="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/5] w-full bg-surface" />
                    <div className="mt-4 h-3 w-2/3 bg-surface" />
                    <div className="mt-2 h-3 w-1/3 bg-surface" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="rounded-none border border-rule bg-surface p-8 text-center" data-testid="category-error">
                <p className="menu-h !border-b-0 !pb-0 !mb-3">Couldn't load the stones just now</p>
                <p className="mx-auto max-w-lg text-[15px] leading-[1.75] text-dim">
                  Our catalogue service didn't answer — usually a brief connection blip. Nothing on your
                  side is broken.
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="btn btn-primary mt-6"
                  data-testid="category-retry"
                >
                  {isFetching ? "Retrying…" : "Try again"}
                </button>
              </div>
            ) : items.length ? (
              <>
                <p className="ui mb-4 text-xs text-dim" data-testid="result-count">
                  Showing <span className="num text-ink">{items.length}</span>{" "}
                  {items.length === 1 ? "item" : "items"}
                </p>
                <ProductGrid products={items} />
              </>
            ) : (
              (() => {
                // Launch-day empty state: if no filters are applied, this
                // category is simply not stocked yet. Show "Coming soon"
                // instead of a filter-clearing prompt.
                const hasFilters =
                  Object.values(facets).some((v) => v && (Array.isArray(v) ? v.length : true)) ||
                  priceRange[0] != null ||
                  priceRange[1] != null;
                if (!hasFilters) {
                  return (
                    <div
                      className="border border-rule bg-surface p-10 text-center"
                      data-testid="category-coming-soon"
                    >
                      <p className="eyebrow-wide">Coming soon</p>
                      <p className="mx-auto mt-4 max-w-[var(--measure-read)] text-[15px] leading-[1.75] text-dim">
                        This shelf is being prepared for launch. Each piece goes live once its
                        certification and photograph are in. Message us about anything specific.
                      </p>
                      <Link
                        to="/contact"
                        className="btn btn-secondary mt-6"
                        data-testid="category-coming-soon-contact"
                      >
                        Ask us about a piece
                      </Link>
                    </div>
                  );
                }
                return (
                  <div>
                    <EmptyState title="Nothing matches those filters">
                      Try clearing one of your filters, or{" "}
                      <Link to="/consult/suitability-session" className="link-u">
                        book The Proof-First Session — ₹499
                      </Link>{" "}
                      — a named consultant can also tell you that you need nothing.
                    </EmptyState>
                    <div className="mt-5 text-center">
                      <button
                        type="button"
                        onClick={() => { setFacets({}); setPriceRange([null, null]); }}
                        className="btn btn-secondary"
                        data-testid="see-every-cta"
                      >
                        See every certified {cfg.stoneMeta?.name?.toLowerCase() || (cfg.kind === "rudraksha" ? "rudraksha" : "stone")}
                      </button>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </Container>
      </Section>

      <AnimatePresence>
        {drawer && (
          <div key="filter-drawer" className="fixed inset-0 z-50 lg:hidden" data-testid="filter-drawer">
            <motion.div
              variants={scrim}
              initial="hidden"
              animate="show"
              exit="exit"
              className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
              onClick={() => setDrawer(false)}
            />
            <motion.div
              variants={drawerBottom}
              initial="hidden"
              animate="show"
              exit="exit"
              className="absolute bottom-0 left-0 right-0 flex max-h-[85vh] flex-col border-t border-rule bg-wash"
            >
              <div className="flex items-center justify-between border-b border-rule px-6 py-4">
                <p className="ui text-sm font-medium">Filter</p>
                <button type="button" onClick={() => setDrawer(false)} aria-label="Close filters" data-testid="close-filter-drawer">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">{rail}</div>
              <div className="border-t border-rule px-6 py-4">
                <button type="button" onClick={() => setDrawer(false)} className="btn btn-primary w-full" data-testid="apply-filters">
                  Show {items.length} {items.length === 1 ? "result" : "results"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Section className="border-t border-rule bg-surface">
        <Container>
          <Reveal>
            <Eyebrow>Everything worth knowing about this category</Eyebrow>
          </Reveal>
          <Stagger className="mt-10 grid gap-x-16 gap-y-12 lg:grid-cols-2" stagger={0.06}>
            {sections.map((s) => (
              <StaggerItem key={s.h} as="article">
                <H2 className="h2-sub">{s.h}</H2>
                <p className="mt-4 text-[15px] leading-[1.75] text-dim">{s.p}</p>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-24">
            <H2>Questions people actually ask</H2>
          </Reveal>
          <Stagger className="mt-10 divide-y divide-rule" stagger={0.04} as="dl" data-testid="category-faqs">
            {faqs.map((f) => (
              <StaggerItem key={f.q} className="py-6">
                <dt><H3>{f.q}</H3></dt>
                <dd className="mt-2.5 text-[15px] leading-[1.75] text-dim">{f.a}</dd>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Section>
    </>
  );
}
